import { AsyncPipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { DownloadAttachmentModalComponent } from '@app/admin/manage/modals/download-attachment-modal/download-attachment-modal.component';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { Chip, MdsChipComponent, MdsDividerComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Expense } from '@models/request';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import { filter } from 'rxjs';
import { computeAuthorizedAmountPatientsCurrencyChip, computeAuthorizedAmountUSDChip } from '../expense-chips.util';

@Component({
    selector: 'medpace-other-expense-view',
    imports: [AsyncPipe, dateFormatPipe, UpperCasePipe, DecimalPipe, MdsDividerComponent, MdsChipComponent],
    templateUrl: './other-expense-view.component.html',
    styleUrl: './other-expense-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtherExpenseViewComponent {
    expense = input.required<Expense>();
    expense$ = toObservable(this.expense).pipe(filter(Boolean));
    elevatedAccess$ = inject(UserServiceV2).permissions.request.access.elevated$;
    requestService = inject(ReimbursementRequestService);
    isInProcessOrHigher$ = this.requestService.isInProcessOrHigher$;
    patient = toSignal(inject(PatientStateService).getPatient());

    private readonly patientPreferredCurrency = computed(
        () => this.patient()?.preferredCurrency?.toUpperCase() ?? null
    );

    authorizedAmountPatientsCurrencyChip = computed((): Chip[] | null =>
        computeAuthorizedAmountPatientsCurrencyChip(this.expense(), this.patientPreferredCurrency())
    );

    authorizedAmountUSDChip = computed((): Chip[] | null => computeAuthorizedAmountUSDChip(this.expense()));
    dialog = inject(MatDialog);

    downloadFile() {
        const patient = this.patient();
        const request = this.requestService.getStateValue();
        if (!request) throw new Error('Request missing!');
        if (!patient) throw new Error('Patient missing!');
        const dataSourceUrl = this.requestService
            .getAttachmentUri({
                requestId: request.id,
                attachmentId: this.expense().attachmentId,
            })
            .toString();
        const attachmentName = `${patient.patientIdentification.firstName}-${patient.patientIdentification.lastName}-expense-attachment`;

        this.dialog.open<DownloadAttachmentModalComponent, DownloadAttachmentModalComponent['data'], void>(
            DownloadAttachmentModalComponent,
            {
                data: { dataSourceUrl, attachmentName },
                ...getModalConfig(ModalSize.Small),
            }
        );
    }
}
